import React, { FC, useCallback, useContext } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { Link } from 'react-router-dom';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { apiLicense } from '../../resources/api/apiLicense';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { UserContext } from '../../contexts/UserContext';
import { useDialog } from '../../hooks/useDialog';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { LicenseContext } from '../../contexts/LicenseContext';
import { LicensesContext } from '../../contexts/LicensesContext';

interface IProps {
  onClose: () => void;
}

const { useForceRelease } = apiLicense;

export const ReleaseErrorLicense: FC<IProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();
  const { onRefresh } = useContext(LicensesContext);
  const license = useContext(LicenseContext);

  const { organization } = useContext(OrganizationContext);
  const { user } = useContext(UserContext);
  const { isOpen: isForce, openUrl: forceUrl, close: abortForce } = useDialog('force');

  const { execute: forceRelease, Guard: GuardForce } = useForceRelease(organization ?? void 0, user?.id, license?.id);

  const onOfflineForceRelease = useCallback(async () => {
    if (!license?.id) return null;
    try {
      await forceRelease();
      await onRefresh();
      onClose();
    } catch (e) {
      handleError(e);
    }
  }, [license, forceRelease]);

  return (
    <>
      <DialogTitle>{t('licenses.releaseDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>{t('licenses.releaseDialog.errorModeDescription')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common.dialog.close')}
        </Button>
        <GuardForce>
          <DangerousButton component={Link} to={forceUrl} replace={true}>
            {t('licenses.releaseDialog.force.button')}
          </DangerousButton>

          <ConfirmationDialog
            open={isForce}
            title={t('licenses.releaseDialog.force.title')}
            message={t('licenses.releaseDialog.force.message')}
            confirm={onOfflineForceRelease}
            abort={abortForce}
            dangerous
          />
        </GuardForce>
      </DialogActions>
    </>
  );
};
